import { useMemo } from 'react';
import Select, { type StylesConfig, type SingleValue, type MultiValue } from 'react-select';
import { getReactSelectStyles } from '../utils/utils';

export interface CodeDefinitionOption {
  value: string;
  label: string;
  is_active?: boolean;
}

export interface DropdownSelectProps {
  allOptions: CodeDefinitionOption[] | undefined;
  value:
    | CodeDefinitionOption
    | { value: string; label: string }
    | CodeDefinitionOption[]
    | { value: string; label: string }[]
    | string
    | string[]
    | null
    | undefined;
  onChange: (selectedOption: CodeDefinitionOption | CodeDefinitionOption[] | null) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  styles?: StylesConfig;
  isMulti?: boolean;
  small?: boolean;
  isClearable?: boolean;
  [key: string]: any;
}

/**
 * A reusable Select component that handles inactive options.
 *
 * This component ensures that:
 * - Only active options are shown in the dropdown for selection
 * - If the current value is inactive, it's still displayed (but can't be re-selected)
 * - The component handles the complexity of filtering and value transformation
 *
 * Works with code definitions or any list of options with the structure:
 * { value: string, label: string, is_active?: boolean }
 *
 * @example
 * ```tsx
 * const { data: urgencyLevelOptions } = useCodeDefinitionsAsOptions('UrgencyLevel');
 *
 * <Controller
 *   name="urgency_level"
 *   control={control}
 *   render={({ field }) => (
 *     <DropdownSelect
 *       allOptions={urgencyLevelOptions}
 *       value={field.value}
 *       onChange={(option) => field.onChange(option)}
 *       isDisabled={isReadOnly}
 *     />
 *   )}
 * />
 * ```
 */
// Helper function to extract code value from various input types
function extractCodeValue(
  val: string | CodeDefinitionOption | { value: string; label: string }
): string | null {
  if (typeof val === 'string') return val;
  if ('value' in val) return val.value;
  return null;
}

// Helper function to find option by code value
function findOptionByCode(
  codeValue: string,
  allOptions: CodeDefinitionOption[]
): CodeDefinitionOption | null {
  return allOptions.find((option) => option.value === codeValue) || null;
}

export function DropdownSelect({
  allOptions,
  value,
  onChange,
  isDisabled = false,
  isLoading = false,
  placeholder = 'Start typing or select from list...',
  styles,
  isMulti = false,
  small = false,
  isClearable = false,
  ...selectProps
}: DropdownSelectProps) {
  // Use provided styles or generate based on small prop
  const computedStyles = styles || getReactSelectStyles(small);
  // Filter to only show active options in the dropdown
  const activeOptions = useMemo(() => {
    if (!allOptions) return [];
    return allOptions.filter((option) => option.is_active !== false);
  }, [allOptions]);

  // Find the current value option(s) (even if inactive)
  const currentValueOptions = useMemo(() => {
    if (!value || !allOptions) return [];

    // Handle array values (multi-select)
    if (Array.isArray(value)) {
      return value
        .map((val) => {
          const codeValue = extractCodeValue(val);
          if (!codeValue) return null;
          return findOptionByCode(codeValue, allOptions);
        })
        .filter((opt): opt is CodeDefinitionOption => opt !== null);
    }

    // Handle single value
    const codeValue = extractCodeValue(value);
    if (!codeValue) return [];
    const option = findOptionByCode(codeValue, allOptions);
    return option ? [option] : [];
  }, [value, allOptions]);

  // Build the options list: active options + current values if they exist and aren't already included
  const options = useMemo(() => {
    if (!allOptions) return [];

    const optionsSet = new Set(activeOptions.map((opt) => opt.value));
    const result = [...activeOptions];

    // Add any current values that aren't in active options
    currentValueOptions.forEach((opt) => {
      if (!optionsSet.has(opt.value)) {
        result.push(opt);
        optionsSet.add(opt.value);
      }
    });

    return result;
  }, [activeOptions, currentValueOptions, allOptions]);

  // Transform the value for react-select
  const selectValue = useMemo(() => {
    if (isMulti) {
      return currentValueOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        is_active: opt.is_active,
      }));
    }

    // Single select
    if (currentValueOptions.length === 0) return null;
    const opt = currentValueOptions[0];
    return {
      value: opt.value,
      label: opt.label,
      is_active: opt.is_active,
    };
  }, [currentValueOptions, isMulti]);

  const handleChange = (
    selectedOption: SingleValue<CodeDefinitionOption> | MultiValue<CodeDefinitionOption>
  ) => {
    if (!selectedOption) {
      onChange(null);
      return;
    }

    if (isMulti) {
      // Multi-select: selectedOption is an array
      const selected = (selectedOption as MultiValue<CodeDefinitionOption>)
        .filter((opt) => opt.is_active !== false)
        .map((opt) => ({
          value: opt.value,
          label: opt.label,
          is_active: opt.is_active,
        }));
      onChange(selected);
    } else {
      // Single select: selectedOption is a single value
      const selected = selectedOption as SingleValue<CodeDefinitionOption>;
      if (!selected || selected.is_active === false) {
        onChange(null);
        return;
      }
      onChange({
        value: selected.value,
        label: selected.label,
        is_active: selected.is_active,
      });
    }
  };

  return (
    <Select
      styles={computedStyles}
      placeholder={placeholder}
      options={options}
      value={selectValue}
      onChange={handleChange}
      isDisabled={isDisabled || isLoading}
      isMulti={isMulti}
      getOptionValue={(option: CodeDefinitionOption) => option.value}
      isOptionDisabled={(option: CodeDefinitionOption) => option.is_active === false}
      isClearable={isClearable}
      {...selectProps}
    />
  );
}
